import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Pagination, Table } from 'react-bootstrap';
import { Activity, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import Icon from './Icon.jsx';
import Loader from './Loader';
import {
  useCreateEventMutation,
  useDeleteEventMutation,
  useGetLeagueEventsQuery,
  useUpdateEventClassificationMutation,
  useUpdateEventMutation,
} from '../slices/eventApiSlice';

const METERS_PER_MILE = 1609.34;
const ACTIVITIES_PER_PAGE = 20;

const toDateTimeLocalValue = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const getAthleteName = (athlete) => {
  const fullName = `${athlete?.firstName || ''} ${athlete?.lastName || ''}`.trim();
  return fullName || athlete?.user?.email || 'Unnamed athlete';
};

const emptyForm = () => ({
  athlete: '',
  name: 'Manual Run',
  miles: '',
  startTime: toDateTimeLocalValue(),
  variation: 'standard',
  ruckWeightLbs: '',
});

const ActivityManager = ({ leagueId, athletes = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data: activityData, isLoading, error } = useGetLeagueEventsQuery({
    leagueId,
    page: currentPage,
    limit: ACTIVITIES_PER_PAGE,
  }, { skip: !leagueId });
  const events = activityData?.events || [];
  const totalActivities = activityData?.pagination?.totalActivities || 0;
  const totalPages = activityData?.pagination?.totalPages || 1;
  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();
  const [updateEventClassification, { isLoading: isClassifying }] = useUpdateEventClassificationMutation();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [form, setForm] = useState(emptyForm);
  const [editingEvent, setEditingEvent] = useState(null);

  const firstActivityIndex = (currentPage - 1) * ACTIVITIES_PER_PAGE;
  const firstPageButton = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const pageButtons = Array.from(
    { length: Math.min(5, totalPages) },
    (_, index) => firstPageButton + index
  );

  useEffect(() => {
    if (!form.athlete && athletes.length) {
      setForm((current) => ({ ...current, athlete: athletes[0]._id }));
    }
  }, [athletes, form.athlete]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [leagueId]);

  const buildPayload = (values) => ({
    athlete: values.athlete,
    leagueId,
    name: values.name.trim() || 'Manual Run',
    distance: Number(values.miles) * METERS_PER_MILE,
    startTime: new Date(values.startTime).toISOString(),
  });

  const handleCreate = async (event) => {
    event.preventDefault();
    try {
      const createdEvent = await createEvent(buildPayload(form)).unwrap();
      if (form.variation !== 'standard') {
        await updateEventClassification({
          id: createdEvent._id,
          leagueId,
          variation: form.variation,
          ruckWeightLbs: form.ruckWeightLbs,
          status: 'approved',
        }).unwrap();
      }
      setForm((current) => ({ ...emptyForm(), athlete: current.athlete }));
      setCurrentPage(1);
      toast.success('Activity added.');
    } catch (requestError) {
      toast.error(requestError?.data?.message || 'Failed to add activity.');
    }
  };

  const openEdit = (event) => {
    const classification = event.scoringClassifications?.find((item) => item.league?.toString() === leagueId?.toString());
    setEditingEvent({
      id: event._id,
      athlete: event.athlete?._id || event.athlete,
      name: event.name || 'Run',
      miles: ((event.distance || 0) / METERS_PER_MILE).toFixed(2),
      startTime: toDateTimeLocalValue(event.startTime),
      variation: classification?.variation || 'standard',
      ruckWeightLbs: classification?.ruckWeightLbs || '',
      classificationStatus: classification?.status || 'approved',
    });
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    try {
      await updateEvent({ id: editingEvent.id, ...buildPayload(editingEvent) }).unwrap();
      await updateEventClassification({
        id: editingEvent.id,
        leagueId,
        variation: editingEvent.variation,
        ruckWeightLbs: editingEvent.ruckWeightLbs,
        status: editingEvent.variation === 'standard' ? 'approved' : editingEvent.classificationStatus,
      }).unwrap();
      setEditingEvent(null);
      toast.success('Activity updated.');
    } catch (requestError) {
      toast.error(requestError?.data?.message || 'Failed to update activity.');
    }
  };

  const handleDelete = async (event) => {
    const athleteName = getAthleteName(event.athlete);
    if (!window.confirm(`Delete ${athleteName}'s ${((event.distance || 0) / METERS_PER_MILE).toFixed(2)} mile activity?`)) return;

    try {
      await deleteEvent({ id: event._id, leagueId }).unwrap();
      toast.success('Activity deleted.');
    } catch (requestError) {
      toast.error(requestError?.data?.message || 'Failed to delete activity.');
    }
  };

  return (
    <Card className="modern-card mb-4">
      <div className="modern-card-header compact-header">
        <h2 className="modern-card-title"><Icon icon={Activity} size={18} className="me-2 align-middle" /> Activity Manager</h2>
        <p className="modern-card-subtitle">Create, correct, or remove activities for athletes in this league.</p>
      </div>
      <Card.Body>
        <Form onSubmit={handleCreate} className="row g-3 align-items-end mb-4">
          <Form.Group className="col-12 col-lg-3">
            <Form.Label>Athlete</Form.Label>
            <Form.Select required value={form.athlete} onChange={(event) => setForm({ ...form, athlete: event.target.value })}>
              <option value="">Select athlete</option>
              {athletes.map((athlete) => <option key={athlete._id} value={athlete._id}>{getAthleteName(athlete)}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group className="col-12 col-md-6 col-lg-3">
            <Form.Label>Activity name</Form.Label>
            <Form.Control required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Form.Group>
          <Form.Group className="col-6 col-md-3 col-lg-2">
            <Form.Label>Miles</Form.Label>
            <Form.Control type="number" min="0.01" step="0.01" required value={form.miles} onChange={(event) => setForm({ ...form, miles: event.target.value })} />
          </Form.Group>
          <Form.Group className="col-6 col-md-3 col-lg-2">
            <Form.Label>Variation</Form.Label>
            <Form.Select value={form.variation} onChange={(event) => setForm({ ...form, variation: event.target.value })}>
              <option value="standard">Standard</option><option value="trail">Trail</option><option value="ruck">Ruck</option><option value="stroller">Stroller</option>
            </Form.Select>
          </Form.Group>
          {form.variation === 'ruck' && <Form.Group className="col-6 col-md-3 col-lg-2"><Form.Label>Pack weight (lb)</Form.Label><Form.Control type="number" min="0" max="200" required value={form.ruckWeightLbs} onChange={(event) => setForm({ ...form, ruckWeightLbs: event.target.value })} /></Form.Group>}
          <Form.Group className="col-12 col-md-6 col-lg-3">
            <Form.Label>Date and time</Form.Label>
            <Form.Control type="datetime-local" required value={form.startTime} onChange={(event) => setForm({ ...form, startTime: event.target.value })} />
          </Form.Group>
          <div className="col-12 col-md-6 col-lg-1">
            <Button type="submit" className="w-100" disabled={isCreating} title="Add activity">
              <Icon icon={Plus} size={18} />
              <span className="visually-hidden">Add activity</span>
            </Button>
          </div>
        </Form>

        {error && <Alert variant="danger">{error?.data?.message || 'Activities could not be loaded.'}</Alert>}
        {isLoading ? <Loader /> : events.length ? (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead><tr><th>Date</th><th>Athlete</th><th>Activity</th><th>Miles</th><th>Source</th><th>Variation</th><th className="text-end">Actions</th></tr></thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event._id}>
                    <td>{new Date(event.startTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</td>
                    <td>{getAthleteName(event.athlete)}</td>
                    <td>{event.name || event.activityType || 'Run'}</td>
                    <td>{((event.distance || 0) / METERS_PER_MILE).toFixed(2)}</td>
                    <td>{event.importedFromGarmin ? 'Garmin' : event.importedFromStrava ? 'Strava' : 'Manual'}</td>
                    <td>{(() => { const classification = event.scoringClassifications?.find((item) => item.league?.toString() === leagueId?.toString()); return classification ? `${classification.variation} (${classification.status})` : /trail/i.test(event.activityType || '') ? 'trail (provider)' : 'standard'; })()}</td>
                    <td className="text-end text-nowrap">
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openEdit(event)} title="Edit activity"><Icon icon={Pencil} size={15} /><span className="visually-hidden">Edit</span></Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(event)} disabled={isDeleting} title="Delete activity"><Icon icon={Trash2} size={15} /><span className="visually-hidden">Delete</span></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mt-3">
              <small className="text-secondary">
                Showing {firstActivityIndex + 1}-{Math.min(firstActivityIndex + events.length, totalActivities)} of {totalActivities} activities
              </small>
              {totalPages > 1 && (
                <Pagination className="mb-0" aria-label="Activity pages">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1} />
                  {pageButtons.map((page) => (
                    <Pagination.Item key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                      {page}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages} />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
              )}
            </div>
          </div>
        ) : <div className="text-secondary">No activities have been recorded for this league.</div>}
      </Card.Body>

      <Modal show={Boolean(editingEvent)} onHide={() => setEditingEvent(null)} centered>
        <Form onSubmit={handleUpdate}>
          <Modal.Header closeButton><Modal.Title>Edit Activity</Modal.Title></Modal.Header>
          <Modal.Body className="d-grid gap-3">
            <Form.Group><Form.Label>Athlete</Form.Label><Form.Select required value={editingEvent?.athlete || ''} onChange={(event) => setEditingEvent({ ...editingEvent, athlete: event.target.value })}>{athletes.map((athlete) => <option key={athlete._id} value={athlete._id}>{getAthleteName(athlete)}</option>)}</Form.Select></Form.Group>
            <Form.Group><Form.Label>Activity name</Form.Label><Form.Control required value={editingEvent?.name || ''} onChange={(event) => setEditingEvent({ ...editingEvent, name: event.target.value })} /></Form.Group>
            <Form.Group><Form.Label>Miles</Form.Label><Form.Control type="number" min="0.01" step="0.01" required value={editingEvent?.miles || ''} onChange={(event) => setEditingEvent({ ...editingEvent, miles: event.target.value })} /></Form.Group>
            <Form.Group><Form.Label>Date and time</Form.Label><Form.Control type="datetime-local" required value={editingEvent?.startTime || ''} onChange={(event) => setEditingEvent({ ...editingEvent, startTime: event.target.value })} /></Form.Group>
            <Form.Group><Form.Label>Variation</Form.Label><Form.Select value={editingEvent?.variation || 'standard'} onChange={(event) => setEditingEvent({ ...editingEvent, variation: event.target.value })}><option value="standard">Standard</option><option value="trail">Trail</option><option value="ruck">Ruck</option><option value="stroller">Stroller</option></Form.Select></Form.Group>
            {editingEvent?.variation === 'ruck' && <Form.Group><Form.Label>Pack weight (lb)</Form.Label><Form.Control type="number" min="0" max="200" required value={editingEvent?.ruckWeightLbs || ''} onChange={(event) => setEditingEvent({ ...editingEvent, ruckWeightLbs: event.target.value })} /></Form.Group>}
            {editingEvent?.variation !== 'standard' && <Form.Group><Form.Label>Review status</Form.Label><Form.Select value={editingEvent?.classificationStatus || 'pending'} onChange={(event) => setEditingEvent({ ...editingEvent, classificationStatus: event.target.value })}><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option></Form.Select></Form.Group>}
          </Modal.Body>
          <Modal.Footer><Button variant="outline-secondary" onClick={() => setEditingEvent(null)}>Cancel</Button><Button type="submit" disabled={isUpdating || isClassifying}>{isUpdating || isClassifying ? 'Saving...' : 'Save Activity'}</Button></Modal.Footer>
        </Form>
      </Modal>
    </Card>
  );
};

export default ActivityManager;